import { Injectable } from '@angular/core';
import {
  ComparisonOperator,
  FormulaAction,
  FormulaCondition,
  FormulaExecutionResult,
  FormulaJSON,
  FormulaStep,
  FormattingOperator,
  Operator,
  QuoteImpact,
  UNIT_PRICE_TOKEN,
  applyDiscountPercentToUnitPrice,
  isPercentOfUnitPriceAction,
  quoteImpactFromMetadata,
  resolvedActionQuoteImpact,
} from './formula.types';

/** A step still waiting for its value; the builder shows a calm "finish the steps" state for it. */
export const INCOMPLETE_STEP_ERROR = 'Incomplete formula step';

@Injectable({ providedIn: 'root' })
export class FormulaEngineService {
  execute(formula: FormulaJSON, context: Record<string, number> = {}): FormulaExecutionResult {
    try {
      if (!formula) {
        return { result: 0, error: 'Formula is required' };
      }
      if (formula.logic_mode === 'conditional') {
        return this.finite(this.executeConditional(formula, context));
      }
      if (formula.logic_mode !== 'recipe') {
        return { result: 0, error: 'Unsupported formula logic_mode' };
      }
      if (!Array.isArray(formula.steps) || formula.steps.length === 0) {
        return { result: 0, error: 'Formula has no steps' };
      }
      return this.finite({
        ...this.executeRecipe(formula.steps, context),
        quote_impact: quoteImpactFromMetadata(formula.metadata),
      });
    } catch (err: any) {
      return { result: 0, error: err?.message || 'Formula execution failed' };
    }
  }

  /**
   * A value the formula reads. An unresolved operand is an ERROR, never 0: a price-setting formula
   * that silently read 0 for a variable the line lacks wrote a $0 unit price and saved it. The error
   * makes applyToLine leave the line's price / discount untouched and show "Error". Same rule and
   * message as the backend evaluator: { error: 'Unknown variable X' }.
   */
  private getValue(val: string | number | undefined, context: Record<string, number>): number {
    if (val === undefined || val === null || val === '') {
      throw new Error(INCOMPLETE_STEP_ERROR);
    }
    if (typeof val === 'number') {
      if (!Number.isFinite(val)) {
        throw new Error('Result is not a finite number');
      }
      return val;
    }
    if (Object.prototype.hasOwnProperty.call(context, val) && context[val] !== undefined && context[val] !== null) {
      const n = Number(context[val]);
      if (!Number.isFinite(n)) {
        throw new Error(`Unknown variable ${val}`);
      }
      return n;
    }
    const trimmed = String(val).trim();
    const asNum = trimmed === '' ? NaN : Number(trimmed);
    if (!Number.isFinite(asNum)) {
      throw new Error(`Unknown variable ${val}`);
    }
    return asNum;
  }

  /** A NaN / Infinity result is an error, not a number to write into a quote. */
  private finite(result: FormulaExecutionResult): FormulaExecutionResult {
    if (!result.error && !Number.isFinite(result.result)) {
      return { ...result, result: 0, error: 'Result is not a finite number' };
    }
    return result;
  }

  private executeRecipe(steps: FormulaStep[], context: Record<string, number>): FormulaExecutionResult {
    let current = 0;
    let warning: string | undefined;

    for (const step of steps) {
      switch (step.type) {
        case 'source':
          current = this.getValue(step.value, context);
          break;
        case 'operation': {
          const operandValue = this.getValue(step.operand as string | number, context);
          switch (step.operator) {
            case Operator.ADD:
              current += operandValue;
              break;
            case Operator.SUBTRACT:
              current -= operandValue;
              break;
            case Operator.MULTIPLY:
              current *= operandValue;
              break;
            case Operator.DIVIDE:
              if (operandValue === 0) {
                throw new Error('Division by Zero');
              }
              current /= operandValue;
              break;
            default:
              throw new Error(`Unknown operator: ${step.operator}`);
          }
          break;
        }
        case 'formatting':
          switch (step.operator) {
            case FormattingOperator.CEILING:
              current = Math.ceil(current);
              break;
            case FormattingOperator.FLOOR:
              current = Math.floor(current);
              break;
            case FormattingOperator.ROUND:
              current = Math.round(current);
              break;
            default:
              break;
          }
          break;
      }
    }

    if (current < 0) {
      warning = 'Result is negative';
    }

    return { result: current, warning };
  }

  private executeConditional(
    formula: FormulaJSON,
    context: Record<string, number>,
  ): FormulaExecutionResult {
    const condition = formula.condition as FormulaCondition | undefined;
    const trueAction = formula.true_action as FormulaAction | undefined;
    const falseAction = formula.false_action as FormulaAction | undefined;

    if (!condition?.left || !condition?.operator) {
      return { result: 0, error: 'Incomplete conditional logic' };
    }
    if (
      condition.right === undefined ||
      condition.right === null ||
      condition.right === ''
    ) {
      return { result: 0, error: 'Incomplete conditional logic' };
    }
    if (
      trueAction?.value === undefined ||
      trueAction?.value === null ||
      trueAction?.value === '' ||
      falseAction?.value === undefined ||
      falseAction?.value === null ||
      falseAction?.value === ''
    ) {
      return { result: 0, error: 'Incomplete conditional logic' };
    }

    const left = this.getValue(condition.left, context);
    const right = this.getValue(condition.right as string | number, context);
    let conditionMet = false;

    switch (condition.operator) {
      case ComparisonOperator.GREATER_THAN:
        conditionMet = left > right;
        break;
      case ComparisonOperator.LESS_THAN:
        conditionMet = left < right;
        break;
      case ComparisonOperator.EQUAL_TO:
        // Tolerance, not ===: 0.1 + 0.2 must equal 0.3. The backend evaluator needs the same rule.
        conditionMet = Math.abs(Number(left) - Number(right)) < 1e-9;
        break;
      case ComparisonOperator.GREATER_THAN_OR_EQUAL:
        conditionMet = left >= right;
        break;
      case ComparisonOperator.LESS_THAN_OR_EQUAL:
        conditionMet = left <= right;
        break;
      default:
        return { result: 0, error: `Unknown comparison operator: ${condition.operator}` };
    }

    const action = conditionMet ? trueAction : falseAction;
    const quoteImpact = resolvedActionQuoteImpact(action, formula);
    const result = this.resolveActionResult(action, context, quoteImpact);
    return {
      result,
      quote_impact: quoteImpact,
      warning: result < 0 ? 'Result is negative' : undefined,
    };
  }

  private resolveActionResult(
    action: FormulaAction,
    context: Record<string, number>,
    quoteImpact: QuoteImpact | string = 'none',
  ): number {
    const raw = this.getValue(action.value as string | number, context);
    // Set Discount % writes the percent itself; keep it 0–100 instead of converting to money.
    if (isPercentOfUnitPriceAction(action) && quoteImpact !== 'discount_percent') {
      return applyDiscountPercentToUnitPrice(raw, this.getValue(UNIT_PRICE_TOKEN, context));
    }
    return raw;
  }
}
